import { useState } from 'react';
import useAgenciesList from '../../hooks/useAgenciesList';
import { useFilterOptions } from '../../hooks/useFilterOptions';
import { AddMerchForm } from '../../Components/MerchForm/AddMerchForm';
import { Modal } from '../../Components/Modal/Modal';
import { useLoader } from '../../contexts/LoaderContext';
import { useAlert } from '../../contexts/AlertContext';
import { MerchHeader } from './components/MerchHeader';
import useMerchList from '../../hooks/useMerchList';
import { MerchContent } from './components/MerchContent';
import { merch } from '../../api/merch';
import { AddMerchFormValues } from '../../Components/MerchForm/types';
import { useFormatImageUrl } from '../../utils/formatImageUrl';
import './Merch.scss';

export const Merch = () => {
  const { agenciesList } = useAgenciesList();
  const { merchCategoryOptions } = useFilterOptions();
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const formatImageUrl = useFormatImageUrl();

  const {
    merchData,
    currentPage,
    agencyFilter,
    categoryFilter,
    onAgencyFilterChange,
    onCategoryFilterChange,
    onPageChange,
    onDeleteMerch,
    fetchMerch,
  } = useMerchList({ itemsPerPage: 8 });

  const [isMerchModalOpen, setIsMerchModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMerch, setSelectedMerch] = useState<any>(null);

  const openMerchModal = () => {
    setIsEditMode(false);
    setSelectedMerch(null);
    setIsMerchModalOpen(true);
  };

  const openEditModal = (merchItem: any) => {
    setIsEditMode(true);
    setSelectedMerch(merchItem);
    setIsMerchModalOpen(true);
  };

  const closeMerchModal = () => {
    setIsMerchModalOpen(false);
    setIsEditMode(false);
    setSelectedMerch(null);
  };

  const handleMerchSubmit = async (values: AddMerchFormValues) => {
    showLoader();
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description);
      formData.append('agencyId', values.agency);
      formData.append('merchCategoryId', values.category);

      if (isEditMode && selectedMerch) {
        if (values.image) {
          formData.append('image', values.image);
        }
        await merch.update(selectedMerch.id, formData);
        await fetchMerch();
        showAlert('Merch updated successfully', 'success');
      } else {
        if (!values.image) {
          showAlert('Image is required', 'error');
          hideLoader();
          return;
        }
        formData.append('image', values.image);
        await merch.create(formData);
        await fetchMerch();
        showAlert('Merch created successfully', 'success');
      }

      closeMerchModal();
    } catch (error) {
      const action = isEditMode ? 'update' : 'create';
      const message = (error as any)?.response?.data?.message || `Failed to ${action} merch`;
      showAlert(message, 'error');
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="merch-page">
      <MerchHeader
        agencyFilter={agencyFilter}
        categoryFilter={categoryFilter}
        onAgencyFilterChange={onAgencyFilterChange}
        onCategoryFilterChange={onCategoryFilterChange}
        onAddMerch={openMerchModal}
        agencyOptions={agenciesList.map((a) => ({ value: a.id.toString(), label: a.name }))}
        categoryOptions={merchCategoryOptions}
      />
      <MerchContent
        merchData={merchData}
        currentPage={currentPage}
        onPageChange={onPageChange}
        onDeleteMerch={onDeleteMerch}
        onEditMerch={openEditModal}
      />
      <Modal isOpen={isMerchModalOpen} onClose={closeMerchModal}>
        <AddMerchForm
          onSubmit={handleMerchSubmit}
          onCancel={closeMerchModal}
          isEditMode={isEditMode}
          existingImageUrl={
            selectedMerch?.imageUrl ? formatImageUrl(selectedMerch.imageUrl) : undefined
          }
          initialValues={
            selectedMerch
              ? {
                  name: selectedMerch.name,
                  description: selectedMerch.description,
                  agency: selectedMerch.agency.id.toString(),
                  category: selectedMerch.merchCategory.id.toString(),
                  image: null,
                }
              : undefined
          }
        />
      </Modal>
    </div>
  );
};
